import { OfflineStore } from "./store";
import type { SyncTransport } from "./types";

export interface ConnectivityTarget {
  addEventListener(type: "online", listener: () => void): void;
  removeEventListener(type: "online", listener: () => void): void;
}

export interface SyncEngineOptions {
  clientId: string;
  batchSize?: number;
  isOnline?: () => boolean;
  events?: ConnectivityTarget;
  onError?: (error: unknown) => void;
}

/** Event-driven sync: it never polls and performs no work while offline. */
export class ReconnectSyncEngine {
  private running?: Promise<void>;
  private started = false;
  private readonly online: () => boolean;
  private readonly events?: ConnectivityTarget;
  private readonly onOnline = (): void => { void this.flush().catch((error) => this.options.onError?.(error)); };

  constructor(
    private readonly store: OfflineStore,
    private readonly transport: SyncTransport,
    private readonly options: SyncEngineOptions,
  ) {
    this.online = options.isOnline ?? (() => typeof navigator === "undefined" || navigator.onLine);
    this.events = options.events ?? (typeof window === "undefined" ? undefined : window);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.events?.addEventListener("online", this.onOnline);
  }

  stop(): void {
    if (!this.started) return;
    this.events?.removeEventListener("online", this.onOnline);
    this.started = false;
  }

  /** May be called after a successful login/startup; concurrent calls coalesce. */
  flush(): Promise<void> {
    if (!this.online()) return Promise.resolve();
    if (this.running) return this.running;
    this.running = this.flushBatches().finally(() => { this.running = undefined; });
    return this.running;
  }

  private async flushBatches(): Promise<void> {
    const batchSize = this.options.batchSize ?? 100;
    while (this.online()) {
      const operations = this.store.pendingOperations(batchSize);
      if (!operations.length) return;
      try {
        const response = await this.transport.push({ clientId: this.options.clientId, cursor: this.store.cursor(), operations });
        const sent = new Set(operations.map((operation) => operation.id));
        const acknowledged = response.acknowledged.filter((id) => sent.has(id));
        await this.store.applySyncResult(acknowledged, response.changes ?? [], response.cursor);
        for (const rejection of response.rejected ?? []) {
          if (!rejection.retryable && sent.has(rejection.id)) await this.store.blockRejected(rejection.id, rejection.reason);
        }
        const retryableRejected = (response.rejected ?? []).filter((item) => item.retryable).map((item) => item.id);
        if (retryableRejected.length) {
          await this.store.recordFailure(retryableRejected, "Server requested retry");
          return; // avoid a hot retry loop; next reconnect/manual flush retries.
        }
        if (!acknowledged.length && operations.length) return;
      } catch (error) {
        await this.store.recordFailure(operations.map((operation) => operation.id), error instanceof Error ? error.message : String(error));
        throw error;
      }
    }
  }
}
